import { addToast, Button, SelectOptionType } from '@octano/global-ui';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { Card, Col, Container, Row } from 'reactstrap';
import GoBackButton from '../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../config/routes';
import { useLayoutState } from '../../../../hooks/useLayoutState';
import { useSIESConfig } from '../../../../hooks/useSIESConfig';
import {
  formatOptions,
  getSelectedOption,
} from '../../../../utils/selectFormat';
import { saveStudyPlanRequest } from '../api/study-plan';
import VersionList from '../version';
import StudyPlansForm from './StudyPlanForm';
import { StudyPlanRecordLoader } from './StudyPlanRecordLoader';

const StudyPlanRecordEdit = () => {
  const { hideTabs, hideErrorModal } = useLayoutState();
  useEffect(() => {
    hideTabs();
    hideErrorModal();
  }, [hideTabs, hideErrorModal]);

  let history = useHistory();
  const goBack = useCallback(() => {
    history.push(`${PathsLayouts.academicOffers}/study-plans`);
  }, [history]);

  const { t } = useTranslation();

  const { id: studyPlanId } = useParams<{ id?: string }>();

  const { siesEnabled } = useSIESConfig();

  const onSubmit = useCallback(
    async (values) => {
      if (!studyPlanId) {
        return;
      }
      const response = await saveStudyPlanRequest({
        name: values.name,
        code: values.code,
        siesCode: values.siesCode,
        degreeTitle: values.noTitleObtained ? null : values.degreeTitle,
        academicDegree: values.noAcademicDegree ? null : values.academicDegree,
        degreeTitleFemale: values.noTitleObtained
          ? null
          : values.degreeTitleFemale,
        degreeTitleMale: values.noTitleObtained ? null : values.degreeTitleMale,
        academicDegreeFemale: values.noAcademicDegree
          ? null
          : values.academicDegreeFemale,
        academicDegreeMale: values.noAcademicDegree
          ? null
          : values.academicDegreeMale,
        modalityId: values.modality.value,
        schoolId: values.school.value,
        periodTypeId: values.periodType.value,
        academicLevelId: values.academicLevel.value,
        careerLevelId: values.careerLevel.value,
        hasLaw20903CLRequirement: values.hasLaw20903CLRequirement?.value === 1,
        id: Number(studyPlanId),
        // SIES
        admissionRequirementId: values.admissionRequirement?.value,
        recognizedSemesters: values.recognizedSemesters,
        academicAreaId: values.academicArea?.value,
        highSchoolDiplomaAdmission: values.highSchoolDiplomaAdmission?.value,
        highSchoolGradesAdmission: values.highSchoolGradesAdmission?.value,
        minimumHighSchoolGPA: values?.minimumHighSchoolGPA,
        priorLearningRecognitionAdmission:
          values.priorLearningRecognitionAdmission?.value,
        workExperienceAdmission: values.workExperienceAdmission?.value,
      });
      if (!response.error) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('common.messages.editSuccess'),
        });
        goBack();
      } else {
        return response.error.code;
      }
    },
    [studyPlanId, goBack, t],
  );

  if (!studyPlanId) {
    return (
      <Redirect to={`${PathsLayouts.academicOffers}/study-plans/record/`} />
    );
  }

  return (
    <>
      <Container>
        <Card className="p-3 p-md-4">
          <div className="mb-4">
            <GoBackButton onClick={goBack} />
          </div>
          <StudyPlanRecordLoader studyPlanId={Number(studyPlanId)}>
            {({
              modalities,
              schools,
              periodTypes,
              academicLevels,
              academicAreas,
              admissionRequirements,
              studyPlan,
            }) => {
              let defaultValues;
              if (studyPlan) {
                const {
                  code,
                  siesCode,
                  name,
                  modality,
                  school,
                  periodType,
                  academicLevel,
                  careerLevel,
                  degreeTitle,
                  academicDegree,
                  titleVariants,
                  studyPlanSies,
                  hasLaw20903CLRequirement,
                } = studyPlan;

                const academicLevelOption: SelectOptionType = academicLevel
                  ? getSelectedOption(
                      academicLevel.id,
                      academicLevels.map((e) => ({
                        label: e.name,
                        value: e.id,
                        careerLevels: e.careerLevels,
                      })),
                    ) ?? {
                      label: academicLevel.name,
                      value: academicLevel.id,
                    }
                  : { label: '', value: 0 };

                const defaultCareerLevel = () => {
                  let filtered: SelectOptionType[] = [];
                  if (academicLevelOption?.careerLevels) {
                    filtered = formatOptions(academicLevelOption.careerLevels);
                  }
                  return getSelectedOption(careerLevel?.id, filtered) ?? null;
                };

                defaultValues = {
                  code,
                  siesCode: siesCode?.trim() || '',
                  name,
                  degreeTitle,
                  academicDegree,
                  degreeTitleFemale:
                    titleVariants?.find(
                      (v) => v.side === 'title' && v.gender === 'female',
                    )?.value ?? '',
                  degreeTitleMale:
                    titleVariants?.find(
                      (v) => v.side === 'title' && v.gender === 'male',
                    )?.value ?? '',
                  academicDegreeFemale:
                    titleVariants?.find(
                      (v) => v.side === 'degree' && v.gender === 'female',
                    )?.value ?? '',
                  academicDegreeMale:
                    titleVariants?.find(
                      (v) => v.side === 'degree' && v.gender === 'male',
                    )?.value ?? '',
                  modality: {
                    label: modality?.name,
                    value: modality?.id,
                  },
                  school: {
                    label: school?.name,
                    value: school?.id,
                  },
                  periodType: {
                    label: periodType?.name,
                    value: periodType?.id,
                  },
                  academicLevel: academicLevelOption,
                  careerLevel: defaultCareerLevel(),
                  hasLaw20903CLRequirement: {
                    label: hasLaw20903CLRequirement
                      ? t('studyPlans.form.yes')
                      : t('studyPlans.form.no'),
                    value: hasLaw20903CLRequirement ? 1 : 0,
                  },
                  // SIES
                  academicArea: studyPlanSies?.academicArea
                    ? {
                        label: studyPlanSies?.academicArea?.name,
                        value: studyPlanSies?.academicArea?.id,
                      }
                    : null,
                  recognizedSemesters:
                    studyPlan?.studyPlanSies?.recognizedSemesters,
                  admissionRequirement: studyPlanSies?.admissionRequirement
                    ? {
                        label: studyPlanSies?.admissionRequirement?.name,
                        value: studyPlanSies?.admissionRequirement?.id,
                      }
                    : null,
                  highSchoolDiplomaAdmission:
                    studyPlan?.studyPlanSies?.highSchoolDiplomaAdmission == null
                      ? null
                      : {
                          label: studyPlan?.studyPlanSies
                            ?.highSchoolDiplomaAdmission
                            ? t(`studyPlans.form.yes`)
                            : t(`studyPlans.form.no`),
                          value: studyPlan?.studyPlanSies
                            ?.highSchoolDiplomaAdmission
                            ? 1
                            : 0,
                        },
                  highSchoolGradesAdmission:
                    studyPlan?.studyPlanSies?.highSchoolGradesAdmission == null
                      ? null
                      : {
                          label: studyPlan?.studyPlanSies
                            ?.highSchoolGradesAdmission
                            ? t(`studyPlans.form.yes`)
                            : t(`studyPlans.form.no`),
                          value: studyPlan?.studyPlanSies
                            ?.highSchoolGradesAdmission
                            ? 1
                            : 0,
                        },
                  minimumHighSchoolGPA:
                    studyPlan?.studyPlanSies?.minimumHighSchoolGPA,
                  priorLearningRecognitionAdmission:
                    studyPlan?.studyPlanSies
                      ?.priorLearningRecognitionAdmission == null
                      ? null
                      : {
                          label: studyPlan?.studyPlanSies
                            ?.priorLearningRecognitionAdmission
                            ? t(`studyPlans.form.yes`)
                            : t(`studyPlans.form.no`),
                          value: studyPlan?.studyPlanSies
                            ?.priorLearningRecognitionAdmission
                            ? 1
                            : 0,
                        },
                  workExperienceAdmission:
                    studyPlan?.studyPlanSies?.workExperienceAdmission == null
                      ? null
                      : {
                          label: studyPlan?.studyPlanSies
                            ?.workExperienceAdmission
                            ? t(`studyPlans.form.yes`)
                            : t(`studyPlans.form.no`),
                          value: studyPlan?.studyPlanSies
                            ?.workExperienceAdmission
                            ? 1
                            : 0,
                        },
                };
              }
              return (
                <>
                  <StudyPlansForm
                    modalities={modalities}
                    periodTypes={periodTypes}
                    schools={schools}
                    academicLevels={academicLevels}
                    academicAreas={academicAreas}
                    admissionRequirements={admissionRequirements}
                    defaultValues={defaultValues}
                    onSubmit={onSubmit}
                    siesEnabled={siesEnabled}
                    isUpdate
                  >
                    {({ isSubmitting, isDirty }) => (
                      <Row className="pt-5">
                        <Col xs={12} md={4} className="ml-auto">
                          <div>
                            <Button
                              type="submit"
                              text={t('common.actions.saveChanges')}
                              loading={isSubmitting}
                              disabled={!isDirty}
                              fullwidth
                            />
                          </div>
                        </Col>
                      </Row>
                    )}
                  </StudyPlansForm>
                  {!!studyPlanId && (
                    <VersionList
                      planStudy={{ id: +studyPlanId }}
                      siesEnabled={siesEnabled}
                    />
                  )}
                </>
              );
            }}
          </StudyPlanRecordLoader>
        </Card>
      </Container>
    </>
  );
};

export default StudyPlanRecordEdit;
